// src/components/resources/tools/generators/BuyerPersona/PersonaOutput.tsx

import { type ReactNode } from 'react'
import { clsx } from 'clsx'
import { safeString } from '~/lib/utils/formatters'
import { type PersonaData, usePersona } from '~/components/resources/tools/generators/BuyerPersona/PersonaContext'
import { properCapitalizeTitleCase, getChannelIcon, getPlatformIcon, getDeviceEmoji, getContentEmoji, getDigitalInfluenceIcon } from '~/components/resources/tools/generators/BuyerPersona/personaUtils'
import { 
  FacebookIcon, LinkedInIcon, TikTokIcon, UserTargetingIcon, LookalikeTargetingIcon, GoogleColorIcon,
  SearchIcon,
  ClockIcon,
  SalePromoIcon,
  ShoppingBasketIcon,
  ScalesOfJusticeIcon,
  GiftIcon,
  VIPIcon,
  CommunicationIcon,
  StarIcon
} from '~/components/resources/tools/generators/BuyerPersona/Icons'


// Define more specific types
type StringOrStringArray = string | string[]

/**
 * PersonaOutput component displays the generated buyer persona
 * with a well-structured and visually appealing layout
 */
export function PersonaOutput(): JSX.Element {
  const { formData, personaOutputRef } = usePersona()
  
  // Type the form data properly
  const data: PersonaData = formData
  
  // Extract business type for conditional rendering
  const businessType = data.businessType ?? ''
  const isB2B = businessType === 'B2B' || businessType === 'B2G'
  const isB2C = businessType === 'B2C' || (typeof businessType === 'string' && businessType.toLowerCase().includes('d2c'))
  
  /**
   * Helper function to check if a section has content
   */
  const hasContent = (fields: Array<keyof PersonaData>): boolean => {
    return fields.some(field => {
      const value = data[field]
      if (value === undefined || value === null) return false
      if (typeof value === 'string' && value.trim() === '') return false
      if (Array.isArray(value) && value.length === 0) return false
      return true
    })
  }
  
  /**
   * Helper to format data for display, handling different types safely
   */
  const safeFormatForDisplay = (value: unknown): string | undefined => {
    if (value === undefined || value === null) return undefined
    
    if (typeof value === 'string') {
      return value
    }
    
    if (Array.isArray(value)) {
      return value.map(String).join('\n')
    }
    
    // For other types, use safeString to avoid [object Object] issues
    return safeString(value, { 
      fallback: undefined,
      tryJSON: true,
      objectFallback: '[Data]',
      arrayFallback: '[List]'
    })
  }

  /**
   * Format a list of items as badges
   */
  const formatList = (items: StringOrStringArray): ReactNode => {
    if (!items) return null
    
    const itemArray = Array.isArray(items) ? items : [items]
    const validItems = itemArray.filter((item): item is string => typeof item === 'string' && item.length > 0)
    
    if (validItems.length === 0) return null
    
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {validItems.map((item, i) => (
          <span 
            key={i} 
            className="badge list-badge px-3 py-1.5 bg-white dark:bg-gray-700 text-blue-700 dark:text-blue-200 rounded-full text-sm font-medium border border-blue-100 dark:border-blue-700">
            {item}
          </span>
        ))}
      </div>
    )
  }

  /**
   * Format a list of communication channels with icons
   */
  const formatChannelList = (channels: StringOrStringArray): ReactNode => {
    if (!channels) return null
    
    const channelArray = Array.isArray(channels) ? channels : [channels]
    const validChannels = channelArray.filter((channel): channel is string => typeof channel === 'string' && channel.length > 0)
    
    if (validChannels.length === 0) return null
    
    return (
      <div className="flex flex-wrap gap-2">
        {validChannels.map((channel, i) => (
          <span 
            key={i} 
            className="badge list-badge inline-flex items-center px-3 py-1 bg-white dark:bg-gray-700 border border-violet-100 dark:border-violet-700 text-violet-700 dark:text-violet-200 rounded-full text-sm shadow-sm">
            <span className="mr-1.5 h-5 w-5 flex items-center justify-center">{getChannelIcon(channel)}</span>
            {channel}
          </span>
        ))}
      </div>
    )
  }
  
  /**
   * Render the persona header section
   */
  const renderHeader = (): JSX.Element => {
    // Use personaName as fallback for name - safely convert to string
    const nameValue = data.name ?? data.personaName ?? 'Your Persona'
    const name = safeString(nameValue, { fallback: 'Your Persona' })
    
    // Determine job title based on business type
    const jobTitleValue = isB2B 
      ? (data.jobTitleB2B ?? data.jobTitle ?? '')
      : (data.jobTitleB2C ?? data.jobTitle ?? '')
    const jobTitle = safeString(jobTitleValue, { fallback: '' })
    
    // Get industry from appropriate field
    const industryValue = data.industryType ?? data.industry ?? ''
    const industry = safeString(industryValue, { fallback: '' })
    
    return (
      <div className="persona-header avoid-break mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900 dark:to-indigo-900 border border-blue-100 dark:border-blue-700 rounded-xl shadow-md p-6">
        <div className="flex items-start">
          {/* Avatar placeholder */}
          <div className="w-24 h-24 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center text-white text-3xl font-bold mr-5 shadow-lg">
            {name ? name.charAt(0).toUpperCase() : '?'}
          </div>
          
          <div className="flex-1">
            <div className="flex justify-between items-start">
              <h2 className="persona-header-persona-name-text text-3xl font-bold text-gray-900 dark:text-white mb-3 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 dark:bg-clip-text dark:from-blue-400 dark:to-indigo-400">
                {name}
              </h2>
              
              <div className="flex items-center">
                {data.businessType && (
                  <span className={clsx(
                    "badge inline-flex items-center px-3 py-1 rounded-full text-sm font-medium shadow-sm",
                    isB2B ? "bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-700" :
                    isB2C ? "bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-700" :
                    "bg-purple-100 dark:bg-purple-800 text-purple-800 dark:text-purple-200 border border-purple-200 dark:border-purple-700"
                  )}>
                    {data.businessType} Persona
                  </span>
                )}
                
                {data.companyName && (
                  <span className={clsx(
                    "badge inline-flex items-center px-3 py-1 ml-2 rounded-full text-sm font-medium shadow-sm",
                    "bg-indigo-100 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200 border border-indigo-200 dark:border-indigo-700"
                  )}>
                    {data.companyName}
                  </span>
                )}
              </div>
            </div>
            
            {/* Job Title based on business type */}
            {jobTitle && (
              <div className="flex items-center text-lg text-gray-700 dark:text-gray-300 mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="font-medium">
                  {properCapitalizeTitleCase(jobTitle)}
                </span>
              </div>
            )}
            
            {/* Industry */}
            {industry && (
              <div className="flex items-center text-lg text-gray-700 dark:text-gray-300 mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-indigo-500 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span className="font-medium">
                  {properCapitalizeTitleCase(industry)}
                </span>
              </div>
            )}
            
            {/* Decision Authority based on business type */}
            {renderDecisionAuthority()}

          </div>
        </div>
      </div>
    )
  }
  
  /**
   * Helper to render decision authority based on business type
   */
  const renderDecisionAuthority = (): JSX.Element | null => {
    const decisionAuthority = isB2B
      ? (data.b2bDecisionAuthority ?? data.decisionAuthorityB2B ?? data.decisionAuthority)
      : (data.b2cDecisionAuthority ?? data.decisionAuthorityB2C ?? data.decisionAuthority)
    
    if (!decisionAuthority) return null
    
    return (
      <div className="flex items-center text-lg text-gray-700 dark:text-gray-300 mb-2">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
        <span className="font-medium">
          {properCapitalizeTitleCase(decisionAuthority)}
        </span>
      </div>
    )
  }
  
  /**
   * Render demographics section
   */
  const renderDemographics = (): JSX.Element | null => {
    // Skip section if no demographics data
    const demographicFields = [
      'ageRange', 'gender', 'income', 'education', 'location', 'familyStatus'
    ]
    
    if (!hasContent(demographicFields)) return null
    
    return (
      <div className="mb-8 avoid-break bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-2xl font-semibold mb-5 pb-3 border-b border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100 flex items-center">
          <span className="mr-3 text-2xl">👤</span>
          Demographics
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Age Range */}
          {data.ageRange && (
            <div className="demographic-item bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="flex items-center mb-2">
                <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-800 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 dark:text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Age Range</h3>
              </div>
              <p className="text-gray-700 dark:text-gray-300 ml-12 text-lg">{data.ageRange}</p>
            </div>
          )}
          
          {/* Gender */}
          {data.gender && (
            <div className="demographic-item bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="flex items-center mb-2">
                <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-800 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 dark:text-blue-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Gender</h3>
              </div>
              <p className="text-gray-700 dark:text-gray-300 ml-12 text-lg">{data.gender}</p>
            </div>
          )}
          
          {/* Income */}
          {data.income && (
            <div className="demographic-item bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="flex items-center mb-2">
                <div className="p-2 rounded-full bg-green-100 dark:bg-green-800 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600 dark:text-green-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Income Level</h3>
              </div>
              <p className="text-gray-700 dark:text-gray-300 ml-12 text-lg">{data.income}</p>
            </div>
          )}
          
          {/* Education */}
          {data.education && (
            <div className="demographic-item bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="flex items-center mb-2">
                <div className="p-2 rounded-full bg-indigo-100 dark:bg-indigo-800 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-600 dark:text-indigo-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path d="M12 14l9-5-9-5-9 5 9 5z" />
                    <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998a12.078 12.078 0 01.665-6.479L12 14z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998a12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Education</h3>
              </div>
              <p className="text-gray-700 dark:text-gray-300 ml-12 text-lg">{data.education}</p>
            </div>
          )}
          
          {/* Location */}
          {data.location && (
            <div className="demographic-item bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="flex items-center mb-2">
                <div className="p-2 rounded-full bg-red-100 dark:bg-red-800 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-600 dark:text-red-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Location</h3>
              </div>
              <p className="text-gray-700 dark:text-gray-300 ml-12 text-lg">{data.location}</p>
            </div>
          )}
          
          {/* Family Status */}
          {data.familyStatus && (
            <div className="demographic-item bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700">
              <div className="flex items-center mb-2">
                <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-800 mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-amber-600 dark:text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Family Status</h3>
              </div>
              <p className="text-gray-700 dark:text-gray-300 ml-12 text-lg">{data.familyStatus}</p>
            </div>
          )}
        </div>
      </div>
    )
  }
  
  /**
   * Render psychographics section with visually appealing cards
   */
  const renderPsychographics = (): JSX.Element | null => {
    // Fields to check based on business type
    const valueField = isB2B 
      ? 'valuesB2B' 
      : 'valuesB2C'
      
    const goalField = isB2B 
      ? 'goalsB2B' 
      : 'goalsB2C'
      
    const challengeField = isB2B 
      ? 'challengesB2B' 
      : 'challengesB2C'
    
    const psychographicFields = [
      valueField, 'interests', 'painPoints', goalField, challengeField
    ]
    
    if (!hasContent(psychographicFields)) return null
    
    const sectionTitle = isB2B ? 'Professional Psychographics' : 'Personal Psychographics'
    
    return (
      <div className="mb-8 avoid-break bg-white dark:bg-gray-800 rounded-xl shadow-md border border-purple-200 dark:border-purple-700 p-6">
        <h2 className="text-2xl font-semibold mb-5 pb-3 border-b border-purple-200 dark:border-purple-700 text-gray-900 dark:text-white flex items-center">
          <span className="mr-3 text-2xl">🧠</span>
          {sectionTitle}
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Values Section */}
          {data[valueField] && (
            <div className="psychographic-item bg-blue-50 dark:bg-blue-900 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
              <div className="flex items-center mb-3">
                <div className="p-2 rounded-full bg-white dark:bg-gray-800 mr-3 shadow-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-blue-800 dark:text-blue-200">
                  {isB2B ? 'Professional Values' : 'Personal Values'}
                </h3>
              </div>
              
              <div className="flex flex-wrap gap-2 ml-12">
                {Array.isArray(data[valueField]) && (data[valueField]).map((value, index) => (
                  <span key={index} className="badge px-3 py-1 bg-white dark:bg-gray-700 text-blue-700 dark:text-blue-200 rounded-full text-sm font-medium border border-blue-100 dark:border-blue-700">
                    {value}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Interests Section */}
          {data.interests && (
            <div className="psychographic-item bg-purple-50 dark:bg-purple-900 rounded-lg p-4 border border-purple-100 dark:border-purple-800">
              <div className="flex items-center mb-3">
                <div className="p-2 rounded-full bg-white dark:bg-gray-800 mr-3 shadow-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-purple-800 dark:text-purple-200">
                  Interests
                </h3>
              </div>
              
              <div className="flex flex-wrap gap-2 ml-12">
                {Array.isArray(data.interests) && (data.interests).map((interest, index) => (
                  <span key={index} className="badge px-3 py-1 bg-white dark:bg-gray-700 text-purple-700 dark:text-purple-200 rounded-full text-sm font-medium border border-purple-100 dark:border-purple-700">
                    {interest}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Goals Section */}
          {data[goalField] && (
            <div className="psychographic-item bg-green-50 dark:bg-green-900 rounded-lg p-4 border border-green-100 dark:border-green-800">
              <div className="flex items-center mb-3">
                <div className="p-2 rounded-full bg-white dark:bg-gray-800 mr-3 shadow-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-green-800 dark:text-green-200">
                  {isB2B ? 'Professional Goals' : 'Personal Goals'}
                </h3>
              </div>
              
              <div className="p-2.5 bg-green-50/70 dark:bg-green-900/50 rounded border border-green-100/80 dark:border-green-800/80 ml-12">
                <p className="text-gray-700 dark:text-gray-200 whitespace-pre-line">
                  {data[goalField]}
                </p>
              </div>
            </div>
          )}
          
          {/* Challenges Section */}
          {data[challengeField] && (
            <div className="psychographic-item bg-orange-50 dark:bg-orange-900 rounded-lg p-4 border border-orange-100 dark:border-orange-800">
              <div className="flex items-center mb-3">
                <div className="p-2 rounded-full bg-white dark:bg-gray-800 mr-3 shadow-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-orange-800 dark:text-orange-200">
                  {isB2B ? 'Professional Challenges' : 'Personal Challenges'}
                </h3>
              </div>
              
              <div className="p-2.5 bg-orange-50/70 dark:bg-orange-900/50 rounded border border-orange-100/80 dark:border-orange-800/80 ml-12">
                <p className="text-gray-700 dark:text-gray-200 whitespace-pre-line">
                  {data[challengeField]}
                </p>
              </div>
            </div>
          )}
          
          {/* Pain Points */}
          {data.painPoints && (
            <div className="psychographic-item bg-red-50 dark:bg-red-900 rounded-lg p-4 border border-red-100 dark:border-red-800">
              <div className="flex items-center mb-3">
                <div className="p-2 rounded-full bg-white dark:bg-gray-800 mr-3 shadow-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-red-800 dark:text-red-200">
                  Pain Points
                </h3>
              </div>
              
              <div className="p-2.5 bg-red-50/70 dark:bg-red-900/50 rounded border border-red-100/80 dark:border-red-800/80 ml-12">
                <p className="text-gray-700 dark:text-gray-200 whitespace-pre-line">
                  {data.painPoints}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
  
  /**
   * Render buying behavior section
   */
  const renderBuyingBehavior = (): JSX.Element | null => {
    // Determine decision criteria field based on business type
    const decisionCriteriaField = isB2B 
      ? 'decisionCriteriaB2B'
      : 'decisionCriteriaB2C'
    
    // Extract and validate fields to ensure type safety
    const researchMethods: string[] = Array.isArray(data.researchMethods) 
      ? data.researchMethods 
      : []
      
    // Skip section if no buying behavior data
    const buyingFields = [
      'purchaseFrequency', 'budget', 'researchMethods', 
      decisionCriteriaField, 'objections', 'communicationChannels'
    ]
    
    if (!hasContent(buyingFields)) return null

    // Helper function to safely get decision criteria
    const getDecisionCriteria = (): string[] => {
      const criteria = data[decisionCriteriaField]
      if (Array.isArray(criteria)) {
        return criteria.map(item => String(item))
      }
      if (Array.isArray(data.decisionCriteria)) {
        return data.decisionCriteria.map(item => String(item))
      }
      return []
    }
    
    const decisionCriteria = getDecisionCriteria()
    
    // Build the children array for the grid explicitly
    const gridChildren: JSX.Element[] = []
    
    // Add research methods card if applicable
    if (researchMethods.length > 0) {
      gridChildren.push(
        <div key="researchMethods" className="bg-indigo-50 dark:bg-indigo-900 rounded-md p-3 border border-indigo-100 dark:border-indigo-800 h-full">
          <h5 className="text-sm font-medium text-indigo-700 dark:text-indigo-200 mb-2 flex items-center">
            <SearchIcon size="md" margin="sm" />
            Research Methods
          </h5>
          {formatList(researchMethods)}
        </div>
      )
    }
    
    // Add decision criteria card if applicable
    if ((data.decisionCriteria && Array.isArray(data.decisionCriteria)) ?? 
        (data[decisionCriteriaField] && Array.isArray(data[decisionCriteriaField]))) {
      gridChildren.push(
        <div key="decisionCriteria" className="bg-blue-50 dark:bg-blue-900 rounded-md p-3 border border-blue-100 dark:border-blue-800 h-full">
          <h5 className="text-sm font-medium text-blue-700 dark:text-blue-200 mb-2 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Decision Criteria
          </h5>
          {formatList(decisionCriteria)}
        </div>
      )
    }
    
    
    // Add Objections card if applicable
    if (data.objections) {
      gridChildren.push(
        <div key="objections" className="bg-red-50 dark:bg-red-900 rounded-md p-3 border border-red-100 dark:border-red-800 h-full">
          <h5 className="text-sm font-medium text-red-700 dark:text-red-200 mb-2 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            Objections
          </h5>
          <p className="text-red-900 dark:text-red-100">
            {safeFormatForDisplay(data.objections)}
          </p>
        </div>
      )
    }
    
    // Add Communication Channels card if applicable
    if (data.communicationChannels && Array.isArray(data.communicationChannels) && data.communicationChannels.length > 0) {
      gridChildren.push(
        <div key="communicationChannels" className="bg-violet-50 dark:bg-violet-900 rounded-md p-3 border border-violet-100 dark:border-violet-800 h-full">
          <h5 className="text-sm font-medium text-violet-700 dark:text-violet-200 mb-2 flex items-center">
            <CommunicationIcon margin="sm" />
            Communication Channels
          </h5>
          {formatChannelList(data.communicationChannels)}
        </div>
      )
    }
    
    // Add Purchase Behavior - Combined card for Purchase Frequency and Budget Consciousness
    if (data.purchaseFrequency ?? data.budgetConsciousness) {
      gridChildren.push(
        <div key="purchaseBehavior" className="bg-amber-50 dark:bg-amber-900 rounded-md p-3 border border-amber-100 dark:border-amber-800 h-full">
          <h5 className="text-sm font-medium text-amber-700 dark:text-amber-200 mb-3 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Purchase Behavior
          </h5>
          
          <div className="space-y-3">
            {/* Purchase Frequency */}
            {data.purchaseFrequency && (
              <div className="flex items-center">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-100 dark:bg-amber-800 rounded-full mr-3">
                  <ClockIcon size="sm" tint="warning" />
                </div>
                <div>
                  <span className="text-xs text-amber-600 dark:text-amber-300">Frequency</span>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{data.purchaseFrequency}</p>
                </div>
              </div>
            )}
            
            {/* Budget Consciousness */}
            {data.budgetConsciousness && (
              <div className="flex items-center">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-100 dark:bg-amber-800 rounded-full mr-3">
                  {(() => {
                    const budget = data.budgetConsciousness.toLowerCase()
                    if (budget.includes('extremely')) {
                      return (
                        <SalePromoIcon size="sm" tint="warning" />
                      )
                    }
                    if (budget.includes('very')) {
                      return (
                        <ShoppingBasketIcon size="sm" tint="warning" />
                      )
                    }
                    if (budget.includes('somewhat')) {
                      return (
                        <ScalesOfJusticeIcon size="sm" tint="warning" />
                      )
                    }
                    if (budget.includes('slightly')) {
                      return (
                        <GiftIcon size="sm" tint="warning" />
                      )
                    }
                    if (budget.includes('not')) {
                      return (
                        <VIPIcon size="sm" tint="warning" />
                      )
                    }
                    return (
                      <svg className="h-4 w-4 text-amber-700 dark:text-amber-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )
                  })()}
                </div>
                <div>
                  <span className="text-xs text-amber-600 dark:text-amber-300">Budget</span>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {(() => {
                      const budget = data.budgetConsciousness.toLowerCase()
                      if (budget.includes('extremely')) return "Very price sensitive"
                      if (budget.includes('very')) return "Price sensitive"
                      if (budget.includes('somewhat')) return "Price aware"
                      if (budget.includes('slightly')) return "Value oriented"
                      if (budget.includes('not')) return "Price insensitive"
                      return data.budgetConsciousness
                    })()}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )
    }
    
    return (
      <div className="mb-8 avoid-break bg-white dark:bg-gray-800 rounded-xl shadow-md border border-amber-200 dark:border-amber-700 p-6">
        <h2 className="text-2xl font-semibold mb-5 pb-3 border-b border-amber-200 dark:border-amber-700 text-gray-900 dark:text-white flex items-center">
          <span className="mr-3 text-2xl">🛒</span>
          Buying Behavior
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {gridChildren}
        </div>
      </div>
    )
  }
  
  /**
   * Render optimization and testing section with KPIs and testing variables
   */
  const renderOptimizationTesting = (): JSX.Element | null => {
    // Skip section if no optimization data
    const optimizationFields = [
      'kpis', 'testVariables', 'budgetAllocation', 'successMetrics', 'optimizationStrategies'
    ]
    
    if (!hasContent(optimizationFields)) return null
    
    return (
      <div className="mb-8 avoid-break bg-white dark:bg-gray-800 rounded-xl shadow-md border border-purple-200 dark:border-purple-700 p-6">
        <h2 className="text-2xl font-semibold mb-5 pb-3 border-b border-purple-200 dark:border-purple-700 text-gray-900 dark:text-white flex items-center">
          <span className="mr-3 text-2xl">📊</span>
          Optimization & Testing
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* KPIs */}
          {data.kpis && Array.isArray(data.kpis) && data.kpis.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900 rounded-md p-3 border border-blue-100 dark:border-blue-800 h-full">
              <h5 className="text-sm font-medium text-blue-700 dark:text-blue-200 mb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Key Performance Indicators
              </h5>
              <div className="flex flex-wrap gap-2 mt-2">
                {data.kpis.map((kpi, i) => (
                  <span key={i} className="badge list-badge px-3 py-1.5 bg-white dark:bg-gray-700 text-blue-700 dark:text-blue-200 rounded-full text-sm font-medium border border-blue-100 dark:border-blue-700 flex items-center">
                    {typeof kpi === 'string' ? kpi : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Test Variables */}
          {data.testVariables && Array.isArray(data.testVariables) && data.testVariables.length > 0 && (
            <div className="bg-purple-50 dark:bg-purple-900 rounded-md p-3 border border-purple-100 dark:border-purple-800 h-full">
              <h5 className="text-sm font-medium text-purple-700 dark:text-purple-200 mb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
                Test Variables
              </h5>
              <div className="flex flex-wrap gap-2 mt-2">
                {data.testVariables.map((variable, i) => (
                  <span key={i} className="badge list-badge px-3 py-1.5 bg-white dark:bg-gray-700 text-purple-700 dark:text-purple-200 rounded-full text-sm font-medium border border-purple-100 dark:border-purple-700 flex items-center">
                    {typeof variable === 'string' ? variable : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Budget Allocation */}
          {data.budgetAllocation && (
            <div className="bg-green-50 dark:bg-green-900 rounded-md p-3 border border-green-100 dark:border-green-800 h-full">
              <h5 className="text-sm font-medium text-green-700 dark:text-green-200 mb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Budget Allocation
              </h5>
              <div className="p-2.5 bg-green-50/70 dark:bg-green-900/50 rounded border border-green-100/80 dark:border-green-800/80">
                <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line">{data.budgetAllocation}</p>
              </div>
            </div>
          )}
          
          {/* Success Metrics */}
          {data.successMetrics && (
            <div className="bg-cyan-50 dark:bg-cyan-900 rounded-md p-3 border border-cyan-100 dark:border-cyan-800 h-full">
              <h5 className="text-sm font-medium text-cyan-700 dark:text-cyan-200 mb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Success Metrics
              </h5>
              <div className="p-2.5 bg-cyan-50/70 dark:bg-cyan-900/50 rounded border border-cyan-100/80 dark:border-cyan-800/80">
                <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line">{data.successMetrics}</p>
              </div>
            </div>
          )}
          
          {/* Optimization Strategies */}
          {data.optimizationStrategies && (
            <div className="col-span-1 md:col-span-2 bg-indigo-50 dark:bg-indigo-900 rounded-md p-3 border border-indigo-100 dark:border-indigo-800">
              <h5 className="text-sm font-medium text-indigo-700 dark:text-indigo-200 mb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Optimization Strategies
              </h5>
              <div className="p-2.5 bg-indigo-50/70 dark:bg-indigo-900/50 rounded border border-indigo-100/80 dark:border-indigo-800/80">
                <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line">{data.optimizationStrategies}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
  
  /**
   * Render digital behavior section
   */
  const renderDigitalBehavior = (): JSX.Element | null => {
    // Skip section if no digital behavior data
    const digitalFields = [
      'marketingChannels', 'platforms', 'devices', 'contentTypes', 
      'onlineBehavior', 'digitalInfluence'
    ]
    
    if (!hasContent(digitalFields)) return null
    
    return (
      <div className="mb-8 avoid-break bg-white dark:bg-gray-800 rounded-xl shadow-md border border-blue-200 dark:border-blue-700 p-6">
        <h2 className="text-2xl font-semibold mb-5 pb-3 border-b border-blue-200 dark:border-blue-700 text-gray-900 dark:text-white flex items-center">
          <span className="mr-3 text-2xl">💻</span>
          Digital Footprint
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Platforms */}
          {data.platforms && Array.isArray(data.platforms) && data.platforms.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900 rounded-md p-3 border border-blue-100 dark:border-blue-800 h-full">
              <h5 className="text-sm font-medium text-blue-700 dark:text-blue-200 mb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Platforms & Social Media
              </h5>
              <div className="flex flex-wrap gap-2 mt-2">
                {data.platforms.map((platform, i) => (
                  <span key={i} className="badge list-badge px-2 py-1.5 bg-white dark:bg-gray-700 text-blue-700 dark:text-blue-200 rounded-full text-sm font-medium border border-blue-100 dark:border-blue-700 flex items-center">
                    <span className="mr-1 h-4 w-4 flex items-center justify-center">
                      {getPlatformIcon(platform)}
                    </span>
                    {typeof platform === 'string' ? platform : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Devices */}
          {data.devices && Array.isArray(data.devices) && data.devices.length > 0 && (
            <div className="bg-green-50 dark:bg-green-900 rounded-md p-3 border border-green-100 dark:border-green-800 h-full">
              <h5 className="text-sm font-medium text-green-700 dark:text-green-200 mb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Device Usage
              </h5>
              <div className="flex flex-wrap gap-2 mt-2">
                {data.devices.map((device, i) => {
                  const deviceName = typeof device === 'string' ? device : ''
                  return (
                    <span key={i} className="badge list-badge px-3 py-1.5 bg-white dark:bg-gray-700 text-green-700 dark:text-green-200 rounded-full text-sm font-medium border border-green-100 dark:border-green-700 flex items-center">
                      <span className="mr-2">{getDeviceEmoji(deviceName)} </span>
                      {deviceName}
                    </span>
                  )
                })}
              </div>
            </div>
          )}
          
          {/* Content Types */}
          {data.contentTypes && Array.isArray(data.contentTypes) && data.contentTypes.length > 0 && (
            <div className="bg-purple-50 dark:bg-purple-900 rounded-md p-3 border border-purple-100 dark:border-purple-800 h-full">
              <h5 className="text-sm font-medium text-purple-700 dark:text-purple-200 mb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                Content Consumption
              </h5>
              <div className="flex flex-wrap gap-2 mt-2">
                {data.contentTypes.map((content, i) => {
                  const contentName = typeof content === 'string' ? content : ''
                  return (
                    <span key={i} className="badge list-badge px-3 py-1.5 bg-white dark:bg-gray-700 text-purple-700 dark:text-purple-200 rounded-full text-sm font-medium border border-purple-100 dark:border-purple-700 flex items-center">
                      <span className="mr-1">{getContentEmoji(contentName)} </span>
                      {contentName}
                    </span>
                  )
                })}
              </div>
            </div>
          )}

          {/* Digital Influence */}
          {data.digitalInfluence && (
            <div className="bg-teal-50 dark:bg-teal-900 rounded-md p-3 border border-teal-100 dark:border-teal-800 h-full">
              <h5 className="text-sm font-medium text-teal-700 dark:text-teal-200 mb-2 flex items-center">
                <StarIcon margin="sm" />
                Digital Influence
              </h5>
              <div className="p-2.5 bg-teal-50/70 dark:bg-teal-900/50 rounded border border-teal-100/80 dark:border-teal-800/80 flex items-center">
                <span className="mr-2 h-5 w-5 flex items-center justify-center">{getDigitalInfluenceIcon(data.digitalInfluence)}</span><p className="text-sm text-gray-700 dark:text-gray-200">{data.digitalInfluence}</p>
              </div>
            </div>
          )}
          
          {/* Online Behavior */}
          {data.onlineBehavior && (
            <div className="md:col-span-2 bg-emerald-50 dark:bg-emerald-900 rounded-md p-3 border border-emerald-100 dark:border-emerald-800 h-full">
              <h5 className="text-sm font-medium text-emerald-700 dark:text-emerald-200 mb-2 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
                Online Behavior
              </h5>
              <div className="p-2.5 bg-emerald-50/70 dark:bg-emerald-900/50 rounded border border-emerald-100/80 dark:border-emerald-800/80">
                <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line">{data.onlineBehavior}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
  
  /**
   * Render targeting recommendations section
   */
  const renderTargetingRecommendations = (): JSX.Element | null => {
    // Skip section if no targeting data
    const targetingFields = [
      'metaTargeting', 'googleTargeting', 'linkedinTargeting', 
      'tiktokTargeting', 'customAudiences', 'lookalikeStrategy'
    ]
    
    if (!hasContent(targetingFields)) return null
    
    return (
      <div className="mb-8 avoid-break bg-white dark:bg-gray-800 rounded-xl shadow-md border border-blue-200 dark:border-blue-700 p-6">
        <h2 className="text-2xl font-semibold mb-5 pb-3 border-b border-blue-200 dark:border-blue-700 text-gray-900 dark:text-white flex items-center">
          <span className="mr-3 text-2xl">🎯</span>
          Ad Platform Targeting Recommendations
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Meta/Facebook Targeting */}
          {data.metaTargeting && (
            <div className="bg-blue-50 dark:bg-blue-900 rounded-md p-3 border border-blue-100 dark:border-blue-800 h-full">
              <h5 className="text-sm font-medium text-blue-700 dark:text-blue-200 mb-2 flex items-center">
                <span className="mr-2 h-5 w-5 flex items-center justify-center">
                  <FacebookIcon size="sm" margin="xs" />
                </span>
                Meta Targeting
              </h5>
              <div className="p-2.5 bg-blue-50/70 dark:bg-blue-900/50 rounded border border-blue-100/80 dark:border-blue-800/80">
                <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line">{data.metaTargeting}</p>
              </div>
            </div>
          )}
          
          {/* Google Targeting */}
          {data.googleTargeting && (
            <div className="bg-amber-50 dark:bg-amber-900 rounded-md p-3 border border-amber-100 dark:border-amber-800 h-full">
              <h5 className="text-sm font-medium text-amber-700 dark:text-amber-200 mb-2 flex items-center">
                <span className="mr-2 h-5 w-5 flex items-center justify-center">
                  <GoogleColorIcon size="sm" margin="xs" />
                </span>
                Google Targeting
              </h5>
              <div className="p-2.5 bg-amber-50/70 dark:bg-amber-800/60 rounded border border-amber-100/80 dark:border-amber-600/80">
                <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line">{data.googleTargeting}</p>
              </div>
            </div>
          )}
          
          {/* LinkedIn Targeting */}
          {data.linkedinTargeting && (
            <div className="bg-sky-50 dark:bg-sky-900 rounded-md p-3 border border-sky-100 dark:border-sky-800 h-full">
              <h5 className="text-sm font-medium text-sky-700 dark:text-sky-200 mb-2 flex items-center">
                <span className="mr-2 h-5 w-5 flex items-center justify-center">
                  <LinkedInIcon size="sm" margin="xs" />
                </span>
                LinkedIn Targeting
              </h5>
              <div className="p-2.5 bg-blue-50/70 dark:bg-blue-900/50 rounded border border-blue-100/80 dark:border-blue-800/80">
                <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line">{data.linkedinTargeting}</p>
              </div>
            </div>
          )}
          
          {/* TikTok Targeting */}
          {data.tiktokTargeting && (
            <div className="bg-gray-50 dark:bg-gray-900 rounded-md p-3 border border-gray-100 dark:border-gray-800 h-full">
              <h5 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center">
                <span className="mr-2 h-5 w-5 flex items-center justify-center">
                  <TikTokIcon size="sm" margin="xs" />
                </span>
                TikTok Targeting
              </h5>
              <div className="p-2.5 bg-gray-50/70 dark:bg-gray-900/50 rounded border border-gray-100/80 dark:border-gray-800/80">
                <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line">{data.tiktokTargeting}</p>
              </div>
            </div>
          )}
          
          {/* Custom Audiences */}
          {data.customAudiences && (
            <div className="bg-indigo-50 dark:bg-indigo-900 rounded-md p-3 border border-indigo-100 dark:border-indigo-800 h-full">
              <h5 className="text-sm font-medium text-indigo-700 dark:text-indigo-200 mb-2 flex items-center">
                <span className="mr-2 h-5 w-5 flex items-center justify-center">
                  <UserTargetingIcon size="sm" margin="xs" />
                </span>
                Custom Audience Strategy
              </h5>
              <div className="p-2.5 bg-indigo-50/70 dark:bg-indigo-900/50 rounded border border-indigo-100/80 dark:border-indigo-800/80">
                <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line">{safeFormatForDisplay(data.customAudiences)}</p>
              </div>
            </div>
          )}
          
          {/* Lookalike Strategy */}
          {data.lookalikeStrategy && (
            <div className="bg-cyan-50 dark:bg-cyan-900 rounded-md p-3 border border-cyan-100 dark:border-cyan-800 h-full">
              <h5 className="text-sm font-medium text-cyan-700 dark:text-cyan-200 mb-2 flex items-center">
                <span className="mr-2 h-5 w-5 flex items-center justify-center">
                  <LookalikeTargetingIcon size="sm" margin="xs" />
                </span>
                Lookalike Audience Strategy
              </h5>
              <div className="p-2.5 bg-cyan-50/70 dark:bg-cyan-900/50 rounded border border-cyan-100/80 dark:border-cyan-800/80">
                <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-line">{safeFormatForDisplay(data.lookalikeStrategy)}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
  
  // Main render
  return (
    <div className="persona-output" ref={personaOutputRef}>
      {renderHeader()}
      {renderDemographics()}
      {renderPsychographics()}
      {renderBuyingBehavior()}
      {renderOptimizationTesting()}
      {renderDigitalBehavior()}
      {renderTargetingRecommendations()}
      <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Generated with the AdSights Buyer Persona Generator
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Use this persona to guide your paid media targeting strategies and creative development.
        </p>
      </div>
    </div>
  )
} 
